import React, { useState, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import styles from './OpeningHoursEditor.module.css';

const OpeningHoursEditor = () => {
  const { hotelData, updateSection, loading } = useData();
  const [barHours, setBarHours] = useState([]);
  const [restaurantHours, setRestaurantHours] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (hotelData?.openingHours) {
      setBarHours([...hotelData.openingHours.bar]);
      setRestaurantHours([...hotelData.openingHours.restaurant]);
    }
  }, [hotelData]);

  const handleBarHoursChange = (index, hours) => {
    const updated = [...barHours];
    updated[index] = { ...updated[index], hours };
    setBarHours(updated);
  };

  const handleRestaurantHoursChange = (index, hours) => {
    const updated = [...restaurantHours];
    updated[index] = { ...updated[index], hours };
    setRestaurantHours(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    const updatedHours = {
      bar: barHours,
      restaurant: restaurantHours
    };

    const result = await updateSection('openingHours', updatedHours);
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Opening hours updated successfully!' });
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to update opening hours' });
    }

    setSaving(false);
    
    // Clear message after 3 seconds
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleReset = () => {
    if (hotelData?.openingHours) {
      setBarHours([...hotelData.openingHours.bar]);
      setRestaurantHours([...hotelData.openingHours.restaurant]);
      setMessage({ type: 'info', text: 'Changes reset to last saved version' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading opening hours...</div>;
  }

  return (
    <div className={styles['hours-editor']}>
      <div className={styles['editor-header']}>
        <h2>Opening Hours Management</h2>
        <p>Update the opening hours for the bar and restaurant.</p>
      </div>

      {message.text && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.text}
        </div>
      )}

      <div className={styles['hours-sections']}>
        {/* Bar Hours */}
        <div className={styles['hours-section']}>
          <h3>Bar Opening Hours</h3>
          <div className={styles['hours-grid']}>
            {barHours.map((day, index) => (
              <div key={day.day} className={styles['hours-row']}>
                <label className={styles['day-label']}>{day.day}</label>
                <input
                  type="text"
                  value={day.hours}
                  onChange={(e) => handleBarHoursChange(index, e.target.value)}
                  className={styles['hours-input']}
                  placeholder="e.g., 5:00 PM - 11:00 PM or Closed"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Restaurant Hours */}
        <div className={styles['hours-section']}>
          <h3>Restaurant Opening Hours</h3>
          <div className={styles['hours-grid']}>
            {restaurantHours.map((day, index) => (
              <div key={day.day} className={styles['hours-row']}>
                <label className={styles['day-label']}>{day.day}</label>
                <input
                  type="text"
                  value={day.hours}
                  onChange={(e) => handleRestaurantHoursChange(index, e.target.value)}
                  className={styles['hours-input']}
                  placeholder="e.g., 5:30 PM - 8:30 PM or Closed"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles['editor-actions']}>
        <button
          onClick={handleReset}
          className={styles['reset-button']}
          disabled={saving}
        >
          Reset Changes
        </button>
        <button
          onClick={handleSave}
          className={styles['save-button']}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className={styles['help-text']}>
        <h4>Tips:</h4>
        <ul>
          <li>Use "Closed" for days when the venue is not open</li>
          <li>Use format like "5:00 PM - 11:00 PM" for opening times</li>
          <li>Changes are saved locally until you deploy updates to the live site</li>
        </ul>
      </div>
    </div>
  );
};

export default OpeningHoursEditor;